import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { FlexyIconButton, ModalContext } from '@europrocurement/flexy-components';
import { editIcon } from '@europrocurement/l2d-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { DossierOpenSearch } from '@europrocurement/l2d-domain';
import type { UpdateDossierPayload } from '@europrocurement/l2d-modules/modules/Dossiers/types/UpdateDossierPayload';
import { EditFolioSocieteRefForm } from '../../../shared/EditFolioSocieteRefForm/EditFolioSocieteRefForm';

interface ClientReferenceProps {
    societeRef?: string;
    dossierSelected?: DossierOpenSearch;
    onUpdate: (payload: UpdateDossierPayload) => void;
    onRefresh?: () => void;
}

export const ClientReference: React.FunctionComponent<ClientReferenceProps> = function ({
    societeRef,
    dossierSelected,
    onUpdate,
    onRefresh,
}) {
    const { modalActions } = React.useContext(ModalContext);
    const [refHovered, setRefHovered] = useState(false);

    const handleCancel = useCallback(() => {
        modalActions.close();
    }, [modalActions]);

    const handleSuccess = useCallback(async () => {
        try {
            setRefHovered(false);
            modalActions.close();
            await new Promise((resolve) => {
                setTimeout(resolve, 2500);
            });
            if (typeof onRefresh !== 'undefined') {
                onRefresh();
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [modalActions, onRefresh]);

    const openEditFolioSocieteRefForm = useCallback(
        (dossier: DossierOpenSearch) => {
            modalActions.call(
                <EditFolioSocieteRefForm
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                    dossier={dossier}
                    updateDossier={onUpdate}
                />,
            );
        },
        [handleCancel, handleSuccess, modalActions, onUpdate],
    );

    const editFolioSocieteRef = useCallback(() => {
        if (!dossierSelected) return;
        openEditFolioSocieteRefForm(dossierSelected);
    }, [openEditFolioSocieteRefForm, dossierSelected]);

    const handleDeleteSocieteRef = useCallback(async () => {
        try {
            onUpdate({
                societeRef: '',
            });
            setRefHovered(false);
            await new Promise((resolve) => {
                setTimeout(resolve, 2500);
            });
            if (typeof onRefresh !== 'undefined') {
                onRefresh();
            }
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
    }, [onUpdate, onRefresh]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                padding: '4px 0',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={() => setRefHovered(true)}
            onMouseLeave={() => setRefHovered(false)}
        >
            <Typography
                variant="l2dbodys"
                color="text.gris60"
            >
                Référence client:
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {societeRef ? (
                    <Typography
                        variant="l2dbodys"
                        color="text.gris70"
                        sx={{ fontWeight: 500 }}
                    >
                        {societeRef}
                    </Typography>
                ) : (
                    <Typography
                        variant="l2dbodys"
                        color="text.gris50"
                        sx={{ fontStyle: 'italic' }}
                    >
                        Non renseignée
                    </Typography>
                )}

                {/* Boutons d'action avec effet slide */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '4px',
                        opacity: refHovered ? 1 : 0,
                        transform: refHovered ? 'translateX(0)' : 'translateX(-8px)',
                        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                >
                    {/* Bouton d'édition */}
                    <FlexyIconButton
                        displayTooltip
                        icon={editIcon}
                        onClick={editFolioSocieteRef}
                        tooltipOverwriteProps={{
                            title: 'Éditer la référence client',
                        }}
                        iconOverwriteProps={{
                            size: '2xs',
                            sx: {
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            },
                        }}
                    />

                    {/* Bouton de suppression (si référence existe) */}
                    {societeRef && (
                        <FlexyIconButton
                            displayTooltip
                            icon={['fasl', 'times'] as IconProp}
                            onClick={handleDeleteSocieteRef}
                            tooltipOverwriteProps={{
                                title: 'Supprimer la référence client',
                            }}
                            iconOverwriteProps={{
                                size: '2xs',
                                sx: {
                                    '&:hover': {
                                        color: 'error.main',
                                    },
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};
